// Palworld TCG / card game long-tail + head keyword cluster.
//
// The TCG lane has the weakest competition of any Palworld query family, so
// every hub tool on the site (/cards, /prices, /deck-builder, /booster-pack,
// /tcg-tools) gets a supporting cluster of guides that each own one specific
// card-game query and link back up to their hub.
//
// Structure mirrors src/lib/guides-longtail.ts: an explicit topic spec expands
// into exactly five AEO-shaped articles (overview, step-by-step, reference,
// mistakes, FAQ) so titles stay unique and answers stay extractable.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-tcg-starter-deck-list",
    title: "Palworld TCG Starter Deck List",
    desc: "Both Palworld Trial Decks card-for-card, what to cut first, and how to turn ETD01 Red・Blue or ETD02 Green・Purple into a legal 50-card deck.",
    keywords: "palworld tcg starter deck list, palworld trial deck card list, palworld tcg deck building for beginners, best palworld starter deck, palworld card game deck list",
    hub: "/deck-builder",
    hubLabel: "Palworld TCG deck builder",
    tag: "TCG Deck",
    question: "What is in a Palworld TCG starter deck?",
    answer:
      "Each Palworld Trial Deck ships 24 distinct cards in a pre-built, ready-to-play list: a two-color Pal line-up, its energy and support cards, and one Soul card. ETD01 is Red・Blue, ETD02 is Green・Purple, and both are tournament-legal out of the box.",
    steps: [
      ["Pick the deck that matches your play style", "ETD01 Red・Blue plays fast, low-cost Pals and direct damage. ETD02 Green・Purple plays slower, ramps energy and wins on board value. Choose the speed you enjoy before you buy singles."],
      ["Load the printed list into the deck builder", "Open the deck builder, filter by set ETD01 or ETD02, and add each printed card number. The validator flags anything over its copy limit immediately."],
      ["Fill to 50 with duplicates you already own", "A Trial Deck is under a full competitive count. Add second and third copies of your best low-cost Pals before adding any new card."],
      ["Cut the dead high-cost cards first", "The single most expensive Pal in each Trial Deck is the first cut. It rarely lands before the game is decided."],
      ["Save and share the list", "Save the deck in the vault so the card codes, colors and costs stay attached, then share the snapshot link instead of a photo of the cards."],
    ],
    data: [
      ["Cards per Trial Deck", "24 distinct cards, including TD-stamped printings and the deck's Soul card."],
      ["ETD01", "Red・Blue — aggressive, cheap Pals, direct damage lines."],
      ["ETD02", "Green・Purple — energy ramp, defensive Pals, value trades."],
      ["Parallels", "Trial Deck cards also exist as TSR and TSP parallels; they are the same card for legality, only the art differs."],
      ["Legality", "A Trial Deck is legal as printed; you never need a booster card to play a sanctioned game."],
    ],
    pitfalls: [
      ["Splitting two Trial Decks into one pile", "Mixing all four colors leaves you unable to pay costs consistently. Keep to the two colors of one deck until you understand the curve."],
      ["Buying chase cards before playables", "An OSR or SSP parallel changes nothing about how the deck plays. Duplicates of cheap Pals win more games."],
      ["Ignoring the Soul card", "The Soul card is a resource, not a bonus. Plan the turn you intend to use it."],
      ["Tracking the list on paper", "Printed lists drift once you swap singles. The deck builder validates copy limits and color costs every time you edit."],
    ],
    faq: [
      ["Is a Palworld TCG starter deck tournament legal?", "Yes. Both Trial Decks are legal exactly as printed, so you can enter a shop event with an unmodified deck."],
      ["How many cards are in a Palworld Trial Deck?", "24 distinct cards per deck, ETD01 Red・Blue and ETD02 Green・Purple."],
      ["Which Palworld starter deck is better for beginners?", "ETD01 Red・Blue, because its cheap Pals let you act on the first few turns while you learn the turn structure."],
      ["Can I mix both Palworld Trial Decks together?", "You can legally, but a four-color deck struggles to pay costs. Combine only after you add extra energy support."],
    ],
  },
  {
    slug: "palworld-card-game-how-to-play",
    title: "Palworld Card Game How to Play",
    desc: "A first-game walkthrough of the Palworld OFFICIAL CARD GAME: setup, turn structure, attacking, Soul cards and how a game actually ends.",
    keywords: "palworld card game how to play, how to play palworld tcg, palworld tcg rules for beginners, palworld card game turn structure, palworld tcg first game",
    hub: "/tcg-tools",
    hubLabel: "Palworld TCG tools",
    tag: "TCG Rules",
    question: "How do you play the Palworld card game?",
    answer:
      "Each player sets up a deck, a life area and an energy area, then alternates turns: draw, place energy, play Pals, attack with one Pal, and end. You win by reducing the opponent's life cards to zero.",
    steps: [
      ["Set up both sides", "Shuffle the deck, set the life cards aside face down, and keep the energy area empty. Both players do this before the first turn."],
      ["Draw your opening hand", "Draw the opening hand and check you have at least one cheap Pal. Without an early play the first two turns are wasted."],
      ["Place one energy per turn", "Energy is the clock. One placement per turn means the cost printed on a card tells you exactly which turn it becomes castable."],
      ["Attack with a single Pal", "Compare the attacker's power against the defender's. Damage that gets through removes a life card."],
      ["Use the Soul card at the swing turn", "Soul cards are one-shot effects. Hold yours for the turn it flips a trade rather than playing it on curve."],
    ],
    data: [
      ["Colors", "Red, Blue, Green, Purple and Colorless. Colorless cards fit any deck."],
      ["Energy", "One energy placement per turn, so a cost-4 Pal lands on turn four at the earliest."],
      ["Attacking", "One attack per turn unless a card grants another."],
      ["Life", "Life is a stack of face-down cards; losing your last one ends the game."],
      ["Counters", "Counter cards resolve during the opponent's turn and are the main reason to hold a card back."],
    ],
    pitfalls: [
      ["Playing every card on curve", "Emptying your hand hands the opponent free counter windows. Keep one reactive card whenever you can."],
      ["Forgetting the energy placement", "Missing one placement delays every play by a full turn — it is the most common beginner loss."],
      ["Attacking into an obvious counter", "If the opponent held a card and passed, assume it is a counter and attack with the Pal you can afford to lose."],
      ["Misreading the card number", "Rarity suffixes such as SSP or TSP change the print, not the rules text. Read the body, not the border."],
    ],
    faq: [
      ["How long does a Palworld TCG game take?", "A two-player game usually runs 15 to 25 minutes once both players know the turn structure."],
      ["How many players can play the Palworld card game?", "Two players per game; shop events run best-of-one or best-of-three rounds."],
      ["Do I need a booster box to start?", "No. One Trial Deck per player is enough for a full legal game."],
      ["What does Colorless mean in Palworld TCG?", "Colorless cards have no color requirement, so they can be paid with any energy and splash into any deck."],
    ],
  },
  {
    slug: "palworld-tcg-card-values-price-guide",
    title: "Palworld TCG Card Values Price Guide",
    desc: "How to price any Palworld TCG card: sold versus listed prices, which rarities hold value, and how the Pal Stars Index tracks the market daily.",
    keywords: "palworld tcg card values, palworld card game price guide, how much are palworld cards worth, palworld tcg card prices today, palworld card value checker",
    hub: "/prices",
    hubLabel: "Palworld TCG card prices",
    tag: "TCG Market",
    question: "How much are Palworld TCG cards worth?",
    answer:
      "Value follows sold prices, not asking prices. Common and Uncommon cards trade near bulk, Rares and Double Rares hold small premiums, and the OSR, SSP and SSS chase printings carry nearly all of a set's value.",
    steps: [
      ["Start from the printed card number", "Look the card up by its exact code, including the rarity suffix. EBP01-001 and EBP01-001SSP are different markets."],
      ["Read sold prices before listed prices", "Listed prices show what sellers hope for. The sold median is the only number a buyer will actually match."],
      ["Check the spread across marketplaces", "A wide gap between venues usually means thin sales volume, so treat the median as a range rather than a price."],
      ["Adjust for condition and grade", "Raw near-mint is the baseline. Graded gem-mint copies trade well above it; anything with edge wear trades below."],
      ["Track the trend, not the day", "Use the price chart's history to see whether a card is climbing or fading before you buy or sell."],
    ],
    data: [
      ["Common / Uncommon", "Bulk value; sells in lots rather than singles."],
      ["Rare / Double Rare", "Small premium, driven by playability more than scarcity."],
      ["SR / SP", "Mid-tier collector demand; foil condition matters."],
      ["OSR / SSP", "Chase parallels — the bulk of a booster box's expected value."],
      ["SSS", "Single ultra-secret gold chase card; priced by scarcity, with very few comparable sales."],
    ],
    pitfalls: [
      ["Pricing from a single listing", "One optimistic listing is not a market. Use the median of recent sales."],
      ["Ignoring the rarity suffix", "Quoting a base card's price for its SSP parallel is the most expensive mistake a new seller makes."],
      ["Assuming graded means gem mint", "Grade tiers differ hugely in value; read the assigned grade, not just the slab."],
      ["Buying into a spike", "Post-release and post-tournament spikes usually settle within weeks."],
    ],
    faq: [
      ["What is the most valuable Palworld TCG card?", "The SSS ultra-secret gold card, followed by the OSR and SSP parallels from Dawn of Palpagos."],
      ["Are Palworld cards a good investment?", "Sealed product and top chase cards hold value best, but any trading card is a speculative purchase — buy what you would keep."],
      ["How often do Palworld card prices update on Pal Stars?", "The price hub syncs sold and listed quotes daily and recomputes the Pal Stars Index from the new medians."],
      ["Why do two sites show different Palworld card prices?", "Different venues have different fees, audiences and sales volume, which is why the hub reports a median across marketplaces."],
    ],
  },
  {
    slug: "palworld-tcg-best-cards-ranked",
    title: "Palworld TCG Best Cards Ranked",
    desc: "Which Palworld TCG cards actually win games, ranked by cost efficiency, color flexibility and how often they show up in winning decks.",
    keywords: "palworld tcg best cards, best palworld card game cards, palworld tcg meta cards, strongest palworld cards, palworld tcg card ranking",
    hub: "/cards",
    hubLabel: "Palworld card database",
    tag: "TCG Meta",
    question: "Which Palworld TCG cards are the best?",
    answer:
      "The best Palworld cards are the ones with the highest power per energy, a color you can reliably pay, and text that still matters late. Chase rarity is irrelevant to strength — parallels are the same card.",
    steps: [
      ["Rank by power per energy first", "Divide printed power by cost across the database. Anything below the curve is a filler card."],
      ["Weight color consistency", "A slightly weaker card in your main color outperforms a stronger off-color card you cannot pay for."],
      ["Read the text for late-game relevance", "Cards whose effect still matters on turn eight belong in the top tier; on-board-only effects fade."],
      ["Check the counter cards separately", "Counters are graded on how often they blow out an attack, not on their own power number."],
      ["Test the shortlist in the deck builder", "Build the list, confirm the curve, then swap one card at a time so you can attribute wins."],
    ],
    data: [
      ["Cost 1-2 Pals", "Judged on board presence — they exist to let you act early."],
      ["Cost 3-4 Pals", "The real deck core; best power-per-energy lives here."],
      ["Cost 5+ Pals", "Only worth a slot if the effect ends the game or stabilises it."],
      ["Soul cards", "One-shot swing effects; every deck plays its best available Soul."],
      ["Counters", "Graded by blowout frequency, not power."],
    ],
    pitfalls: [
      ["Ranking by rarity", "An SSP parallel is identical in play to its base printing. Rarity is a collector axis only."],
      ["Overloading the top of the curve", "Two or three expensive finishers is the ceiling before your early turns collapse."],
      ["Ignoring color requirements", "A best-card list is only useful inside a deck that can pay the costs."],
      ["Copying a list without the counters", "The counter slots are what make winning lists resilient; they are the first thing casual copies drop."],
    ],
    faq: [
      ["What is the strongest Pal card in the Palworld TCG?", "The strongest playables sit in the cost-3 and cost-4 band where power per energy peaks; use the card database filters to sort them for the current set."],
      ["Do SSP and OSR cards hit harder?", "No. Parallel printings have identical rules text and stats — only the art and rarity differ."],
      ["How many copies of one card can I play?", "Copy limits are enforced per card; the deck builder blocks any list that exceeds them."],
      ["Are Trial Deck cards competitive?", "Several are, particularly the cheap Pals. Trial Deck printings are legal and identical to their booster versions."],
    ],
  },
  {
    slug: "palworld-tcg-pull-rates-explained",
    title: "Palworld TCG Pull Rates Explained",
    desc: "Every slot in a Palworld booster pack, the real odds per rarity, and what a sealed box should statistically contain.",
    keywords: "palworld tcg pull rates, palworld booster pack odds, palworld card game pack rates, chance of ssp palworld card, palworld booster box expected value",
    hub: "/booster-pack",
    hubLabel: "Palworld booster pack pull rates",
    tag: "TCG Packs",
    question: "What are the pull rates in a Palworld booster pack?",
    answer:
      "A Palworld booster pack fills seven slots: fixed Common and Uncommon slots, a guaranteed Rare slot, and a rotating hit slot where the higher rarities — R, RR, SR, SP, OSR, SSP and the SSS chase — are actually drawn.",
    steps: [
      ["Identify the slot before the rarity", "Odds are per slot, not per pack. The fixed slots cannot produce a chase card at all."],
      ["Read the hit slot distribution", "Nearly all variance lives in one slot. Compare its rarity weights on the booster pack page."],
      ["Multiply per box, not per pack", "Box expectation is the pack rate times the pack count — single-pack results tell you nothing."],
      ["Compare your own results honestly", "Small samples swing wildly. Log 50+ packs before concluding your rates are unusual."],
      ["Price the box against the expectation", "Multiply each rarity's expected count by its sold median to decide whether sealed or singles is cheaper."],
    ],
    data: [
      ["Slots per pack", "7."],
      ["Fixed slots", "Common and Uncommon fillers plus a guaranteed Rare."],
      ["Hit slot", "Rotating slot that produces RR and above."],
      ["Parallels", "OSR and SSP replace the hit slot rather than adding to the pack."],
      ["SSS", "Single ultra-secret chase card, far rarer than any other slot outcome."],
    ],
    pitfalls: [
      ["Treating pack odds as a guarantee", "Independent packs do not owe you a hit; there is no pity counter."],
      ["Judging rates from one box", "A single box is a tiny sample against the hit-slot distribution."],
      ["Confusing the simulator with sealed odds", "The Pal Stars pack simulator uses published rates, but simulated pulls have no market value."],
      ["Ignoring bulk value", "The Commons and Uncommons still sell in lots; leaving them out understates a box's return."],
    ],
    faq: [
      ["How many cards are in a Palworld booster pack?", "Seven, across fixed Common, Uncommon and Rare slots plus a rotating hit slot."],
      ["What are the odds of pulling an SSP Palworld card?", "SSP appears only in the hit slot and is one of its rarest outcomes; the booster pack page lists the current weighting per rarity."],
      ["Is a booster box guaranteed a chase card?", "No. Box contents follow per-pack odds with no guarantee of any specific rarity."],
      ["Can I test the pull rates for free?", "Yes — the Pal Stars booster pack simulator opens packs using published rates so you can see the distribution without buying sealed product."],
    ],
  },
  {
    slug: "palworld-tcg-set-list-checklist",
    title: "Palworld TCG Set List Checklist",
    desc: "The full printed set list for the Palworld OFFICIAL CARD GAME — Dawn of Palpagos, both Trial Decks and promos — as a checklist you can tick off against your binder.",
    keywords: "palworld tcg set list, palworld card game checklist, dawn of palpagos card list, palworld tcg card numbers list, palworld card set complete list",
    hub: "/cards",
    hubLabel: "Palworld card database",
    tag: "TCG Set List",
    question: "What is the full Palworld TCG set list?",
    answer:
      "The English release covers EBP01 Dawn of Palpagos with its SR, SP, OSR, SSP and SSS parallels, the ETD01 Red・Blue and ETD02 Green・Purple Trial Decks with TSR and TSP parallels, ESOUL Soul cards and EPR promos.",
    steps: [
      ["Sort your binder by set code first", "Group EBP01, ETD01, ETD02, ESOUL and EPR before you count anything — mixed sets cause double-counting."],
      ["Work through the base numbers in order", "Tick the plain numbers (EBP01-001 upward) before touching parallels; the base run defines completion."],
      ["Add parallels as separate lines", "A parallel like EBP01-001SSP is its own checklist entry, not a substitute for the base card."],
      ["Verify each questionable code", "Use the card checker to confirm the set, rarity, color and cost match the card in hand."],
      ["Record the gaps as a want list", "Export the missing numbers and price them on the price hub before buying singles."],
    ],
    data: [
      ["EBP01", "Dawn of Palpagos — 100 base cards plus SR, SP, OSR, SSP and SSS parallels."],
      ["ETD01", "Red・Blue Trial Deck — 24 cards, plus TSR and TSP parallels."],
      ["ETD02", "Green・Purple Trial Deck — 24 cards, plus TSR and TSP parallels."],
      ["ESOUL", "Soul cards, printed with the ESOUL prefix."],
      ["EPR", "Promo cards distributed through events and product bundles."],
    ],
    pitfalls: [
      ["Dropping the leading E", "English printings read EBP01 and ETD01. Bare BP01 or TD01 codes cause mismatched lookups."],
      ["Counting a parallel as the base card", "Parallels are extra entries; a set is not complete because you own its SSP."],
      ["Mixing Japanese and English numbers", "Number runs differ between regions; check the set code before ticking a line."],
      ["Checking off from memory", "Verify the printed lower-band code on the card itself — art alone is not identifying."],
    ],
    faq: [
      ["How many cards are in Dawn of Palpagos?", "100 base cards, plus its SR, SP, OSR, SSP and SSS parallel printings."],
      ["Where is the set code printed on a Palworld card?", "In the lower band of the card face, formatted as set code, dash, collector number — for example EBP01-001."],
      ["Do promos count toward set completion?", "Promos are tracked as their own EPR run rather than part of a booster set."],
      ["How do I verify a Palworld card number?", "Enter the printed code in the Pal Stars card checker and compare the returned set, rarity, color and cost with the card."],
    ],
  },
  {
    slug: "palworld-tcg-soul-card-rules",
    title: "Palworld TCG Soul Card Rules",
    desc: "What Soul cards do in the Palworld card game, when you may play one, and how they interact with counters and attacks.",
    keywords: "palworld tcg soul card rules, how do soul cards work palworld tcg, palworld soul card list, esoul cards palworld, palworld tcg soul card timing",
    hub: "/tcg-tools",
    hubLabel: "Palworld TCG tools",
    tag: "TCG Rules",
    question: "How do Soul cards work in the Palworld TCG?",
    answer:
      "A Soul card is a one-shot effect played from your Soul area rather than your hand. It resolves once, then leaves play, so its value is entirely in the turn you choose to use it.",
    steps: [
      ["Set the Soul card during setup", "Soul cards live in their own area from the start of the game; they are not drawn."],
      ["Confirm the timing window on the card", "Some Souls resolve on your turn only, others react. Read the timing line before you commit."],
      ["Hold it for a swing turn", "Playing a Soul on curve wastes it. The right turn is the one where it converts a losing trade into a winning one."],
      ["Play around counters", "If the opponent is holding cards, expect a counter and sequence the Soul so a counter cannot undo the whole turn."],
      ["Log the outcome", "Track which turn you used it and whether it changed the result; that decides whether the Soul stays in the list."],
    ],
    data: [
      ["Area", "Soul area, separate from deck and hand."],
      ["Uses", "One-shot: resolves once, then leaves play."],
      ["Prefix", "English Soul cards are printed with the ESOUL set prefix, e.g. ESOUL-002."],
      ["Deck slots", "Soul cards do not occupy main-deck card slots."],
      ["Counters", "A counter can interact with the attack a Soul enabled, so sequencing matters."],
    ],
    pitfalls: [
      ["Using the Soul as a curve play", "Spending it early for tempo throws away its best turn."],
      ["Forgetting the Soul exists", "It sits outside the hand, so new players lose games with an unused Soul on the table."],
      ["Assuming Souls cannot be answered", "The board state a Soul creates is still subject to counters and attacks."],
      ["Building a deck that needs the Soul", "The Soul should improve a functioning deck, not hold a broken one together."],
    ],
    faq: [
      ["How many Soul cards can I use in a game?", "Each Soul card resolves once and then leaves play, so plan the single turn you want it on."],
      ["Do Soul cards count toward my 50-card deck?", "No. Soul cards sit in their own area and do not use main-deck slots."],
      ["What does the ESOUL prefix mean?", "It identifies the English Soul card set — for example ESOUL-002 — the same way EBP01 identifies Dawn of Palpagos."],
      ["Can a counter stop a Soul card?", "A counter cannot un-play the Soul, but it can answer the attack or board state the Soul set up, so sequence carefully."],
    ],
  },
  {
    slug: "palworld-tcg-energy-cost-curve",
    title: "Palworld TCG Energy and Cost Curve",
    desc: "How energy works in the Palworld card game and how to build a cost curve that lets you act on every turn instead of stalling.",
    keywords: "palworld tcg energy rules, palworld card game cost curve, how does energy work palworld tcg, palworld tcg mana curve, palworld deck cost distribution",
    hub: "/deck-builder",
    hubLabel: "Palworld TCG deck builder",
    tag: "TCG Deck",
    question: "How does energy work in the Palworld TCG?",
    answer:
      "You place one energy per turn, so the printed cost of a card is also the earliest turn it can be played. A deck's cost curve therefore decides how many turns you spend doing nothing.",
    steps: [
      ["Count your one-drops and two-drops", "You need enough cheap cards that turns one and two are never blank. This is the single biggest win-rate lever."],
      ["Centre the curve on cost 3-4", "That band has the best power per energy, so it should be the largest group in the deck."],
      ["Cap the top end", "Two or three cards above cost five is the maximum before your early game collapses."],
      ["Match colors to the curve", "An off-color cheap Pal you cannot pay for on turn one is not a cheap Pal."],
      ["Validate in the deck builder", "The builder shows the cost distribution as you edit, so fix curve holes before the list leaves the vault."],
    ],
    data: [
      ["Energy per turn", "One placement per turn, every turn."],
      ["Cost = earliest turn", "A cost-4 card is unplayable before turn four."],
      ["Ideal core", "Cost 3-4 Pals, the highest power-per-energy band."],
      ["Cheap slots", "Enough cost 1-2 cards to guarantee an early play."],
      ["Top end", "2-3 cards at cost 5+, no more."],
    ],
    pitfalls: [
      ["Building a deck of only strong cards", "Strength per card without a curve loses to any deck that acts first."],
      ["Missing an energy placement", "One skipped placement pushes every remaining play back a turn."],
      ["Four colors in one list", "Color requirements make the printed cost effectively higher; two colors is the safe default."],
      ["Ignoring the builder's distribution graph", "Curve holes are invisible in a card list and obvious in the distribution view."],
    ],
    faq: [
      ["Can I place more than one energy per turn in Palworld TCG?", "Not by default — one placement per turn, unless a specific card says otherwise."],
      ["What is a good cost curve for a Palworld deck?", "A cheap early block, the bulk of the deck at cost 3-4, and only two or three cards above cost five."],
      ["Does color affect cost in the Palworld card game?", "The number stays the same, but color requirements decide whether you can actually pay it, which is why two-color decks are more consistent."],
      ["How do I check my deck's curve?", "The Pal Stars deck builder shows the cost distribution live while you add and remove cards."],
    ],
  },
  {
    slug: "palworld-tcg-graded-card-values",
    title: "Palworld TCG Graded Card Values",
    desc: "What grading does to a Palworld card's price, which cards are worth submitting, and how graded sold prices compare with raw copies.",
    keywords: "palworld tcg graded card values, palworld card grading worth it, psa palworld cards price, graded palworld card prices, palworld gem mint 10 value",
    hub: "/prices",
    hubLabel: "Palworld TCG card prices",
    tag: "TCG Market",
    question: "Are graded Palworld cards worth more?",
    answer:
      "Grading multiplies value only on cards that already carry a premium. A gem-mint chase parallel can trade far above raw, while a graded Common usually sells for less than the grading fee.",
    steps: [
      ["Filter to cards above the fee threshold", "If a raw copy sells for less than the grading and shipping cost, grading destroys value. Start by pricing raw."],
      ["Inspect the card honestly", "Centring, edges, surface and corners decide the grade. A visible flaw caps the outcome and the return."],
      ["Compare graded sold prices per grade tier", "Prices differ sharply between adjacent grades, so use sold comparables for the specific grade you expect."],
      ["Decide between selling raw and submitting", "Raw sales are immediate; grading is weeks of turnaround plus grade risk."],
      ["Track the graded market after submission", "Chase-card prices move with tournament and reprint news, so watch the trend before listing."],
    ],
    data: [
      ["Commons / Uncommons", "Almost never worth grading — fee exceeds value."],
      ["Rares / Double Rares", "Rarely worth grading unless the card is a format staple."],
      ["SR / SP", "Sometimes worth it for clean foil copies."],
      ["OSR / SSP", "The main grading candidates; grade tiers change price materially."],
      ["SSS", "Highest graded premiums, with very thin sales data."],
    ],
    pitfalls: [
      ["Grading sentimental cards", "Attachment is not value. Price raw first, then decide."],
      ["Assuming a slab means a high grade", "Low graded tiers can sell below raw near-mint. Always read the assigned grade."],
      ["Ignoring turnaround time", "A card can lose value while it sits at the grader."],
      ["Pricing from listings only", "Graded listings are frequently aspirational; use sold comparables."],
    ],
    faq: [
      ["Is it worth grading Palworld cards?", "Only for cards whose raw sold price already exceeds the grading and shipping cost — in practice the OSR, SSP and SSS printings."],
      ["How much does a gem-mint Palworld card sell for?", "It depends entirely on the card; check the graded sold medians on the price hub for the exact printed code."],
      ["Does grading change how a card plays?", "A slabbed card cannot be played in a deck at all — grading is purely a collector decision."],
      ["What lowers a Palworld card's grade?", "Off-centre printing, edge whitening, surface scratches and soft corners are the usual causes."],
    ],
  },
  {
    slug: "palworld-tcg-sealed-investment",
    title: "Palworld TCG Sealed Product Guide",
    desc: "Booster boxes, Trial Decks and bundles compared: what sealed Palworld product costs, what it returns in singles, and when singles are cheaper.",
    keywords: "palworld tcg sealed product, palworld booster box worth it, palworld tcg box vs singles, palworld sealed card prices, palworld booster box price",
    hub: "/prices",
    hubLabel: "Palworld TCG card prices",
    tag: "TCG Market",
    question: "Is a Palworld booster box worth buying?",
    answer:
      "A box is worth it when its expected singles value plus the fun of opening beats the cost of buying the cards you actually want. For a specific playset, singles are almost always cheaper.",
    steps: [
      ["Write down what you actually need", "A deck list first, then a price. Buying sealed without a target is how budgets disappear."],
      ["Price the singles you need", "Total the sold medians for your want list. That is the number sealed has to beat."],
      ["Compute the box expectation", "Multiply each rarity's expected count per box by its sold median, including bulk value for Commons."],
      ["Compare against the sealed price", "If singles cost less than the box, buy singles and keep the difference."],
      ["Track sealed prices over time", "Sealed drifts with print-run news; the price hub records sealed quotes alongside singles."],
    ],
    data: [
      ["Booster pack", "7 cards; the unit all box math is built from."],
      ["Booster box", "Multiple packs; expectation is the per-pack rate times pack count."],
      ["Trial Deck", "24 fixed cards; the cheapest legal entry point."],
      ["Bundles", "Packs plus accessories or promos; value depends on the promo."],
      ["Bulk", "Commons and Uncommons still hold lot value and belong in box math."],
    ],
    pitfalls: [
      ["Chasing one specific card in sealed", "Targeting a single chase card through packs is the most expensive route to owning it."],
      ["Ignoring bulk in the box math", "Leaving Commons out understates the return and makes sealed look worse than it is."],
      ["Buying into a launch spike", "Sealed prices are highest at release and usually settle afterwards."],
      ["Assuming sealed always appreciates", "Reprints and print-run size can flatten sealed value for years."],
    ],
    faq: [
      ["How many packs are in a Palworld booster box?", "Box configuration is printed on the product; multiply the pack count by the seven-card pack contents for box math."],
      ["Is it cheaper to buy Palworld singles or packs?", "For a specific deck list, singles are cheaper almost every time. Sealed only wins when you want the opening experience or broad coverage."],
      ["Does sealed Palworld product hold value?", "Sealed generally holds better than bulk singles, but print-run size and reprints decide the outcome."],
      ["Where can I see sealed Palworld prices?", "The Pal Stars price hub tracks sealed quotes alongside single-card sold and listed prices."],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here feeds the ${t.hubLabel} at ${t.hub}, so keep the tool open beside the guide.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What the card game actually checks", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        { heading: `Use it with the ${t.hubLabel}`, text: `${hubSentence} Open ${t.hub}, apply the filters described below, and work from the tool's output rather than a remembered list.` },
      ],
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The exact order to follow. Each step removes a mistake that costs real money or real games.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `The values and rules behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "Four failure modes that account for most wasted money and lost games on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions players ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(2),
    },
  ];
}

export const TCG_LONGTAIL_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} Guide & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-08-26",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each TCG cluster supports, for internal-link rendering. */
export const TCG_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
